/** @jsx jsx */
import { Modal, Spacer, TextGroupMedium } from "@cd2/components/build/es6"
import { Form2 } from "@cd2/forms/build/es6"
import { jsx } from "@emotion/core"
import { add, format } from "date-fns"
import React, { useEffect, useState } from "react"
import { ConfirmCancelButtons } from "../Components/Buttons/ConfirmCancelButtons"
import { CreateButton } from "../Components/Buttons/CreateButton"
import { FieldsRadioButtonSelect } from "../Components/FieldsRadioCheckbox"
import { UserSelectList } from "../Components/SelectLists/UserSelectList"
import { HoverProjectLoader } from "../hoverLoaders/HoverProjectLoader"
import {
  percentCertaintyOptions,
  projectStages,
  StageCertaintyWatcher,
} from "../Pages/CRM/Projects/ProjectsForm"
import { IRecord } from "../types/schema"
import { useConfirmation } from "./useConfirmation"
import { useSiteAlerts } from "./useSiteAlerts"
import { P } from "@cd2/text"

type IQuote = IRecord<"quotes", "identifier"> & {
  contact?: IRecord<"contacts"> | null
  projects: IRecord<"projects", "name" | "stage">[]
  customer?:
    | (IRecord<"customers", "name"> & { sales_rep?: IRecord<"sales_reps", "user" | "name"> | null })
    | null
}

type IUserRecord = IRecord<"skeem_users", "name" | "skeem_email">

interface IOpts {
  header?: React.ReactNode
  body?: React.ReactNode
}

export function useProjectFromQuote({
  onAfterAssign,
  includeSkip,
  initialQuote,
}: {
  onAfterAssign?: () => void
  includeSkip?: boolean
  initialQuote?: IQuote
}) {
  const alerts = useSiteAlerts()
  const skeem = window.skeem
  const [quote, setQuote] = useState<IQuote | null>(initialQuote || null)

  useEffect(() => {
    if (initialQuote) {
      setQuote(initialQuote)
    }
  }, [initialQuote])

  async function loadQuote(quoteId: string) {
    setQuote(null)
    const data = await skeem.fetch2<IQuote>({
      first: {
        name: "quotes",
        attributes: [
          "identifier",
          { name: "projects", attributes: ["name", "stage"] },
          {
            name: "customer",
            attributes: [{ name: "sales_rep", attributes: ["user", "name"] }, "name"],
          },
          "contact",
        ],
        filter: { matches: { id: quoteId } },
      },
    })
    return data
  }

  async function loadProjects(query: string) {
    return skeem.fetch2<IRecord<"projects", "name">>({
      model: {
        name: "projects",
        attributes: ["name"],
        paginate: { size: 10 },
        filter: { matches: { name: { like: query }, customer: quote?.customer?.id } },
      },
    })
  }

  const [projectNode, handleOpenProjectNode] = useConfirmation<
    { quote?: IQuote; opts?: IOpts },
    | {
        type: "new"
        name: string
        dueDate: Date
        stage: string
        percentCertainty: number
        followUpDate: Date
        reminderUser?: IUserRecord
      }
    | { type: "add"; projectId: string }
    | { type: "skip"; projectId?: never }
    | null
  >(({ data, onResolve }) => {
    const quote = data.quote
    const opts = data.opts
    const [tab, setTab] = useState<"new" | "add" | "skip">("new")
    return (
      <Modal
        render={() => (
          <div>
            <TextGroupMedium
              heading={opts?.header ?? `Add Project to quote ${quote?.identifier}`}
              body={opts?.body}
            />
            <Spacer size={16} />
            <FieldsRadioButtonSelect
              onChange={setTab}
              value={tab}
              options={[
                { label: "New project", value: "new" },
                { label: "Existing project", value: "add" },
                includeSkip && { label: "Skip creating project", value: "skip" },
              ].filter(Boolean)}
              name=""
            />
            <Spacer />
            {tab === "new" && (
              <Form2.Form
                onSubmit={({
                  name,
                  dueDate,
                  stage,
                  percentCertainty,
                  followUpDate,
                  reminderUser,
                }) =>
                  onResolve({
                    type: "new",
                    name,
                    dueDate,
                    stage,
                    percentCertainty,
                    followUpDate,
                    reminderUser,
                  })
                }
                initialData={{
                  followUpDate: add(new Date(), { days: 7 }),
                  stage: "Quoted",
                  name: data.quote?.customer?.name,
                }}
              >
                <Form2.Text
                  field="name"
                  validations={{ presence: true }}
                  placeholder="Project for my customer"
                />
                <Form2.Date
                  field="dueDate"
                  validations={{ presence: true }}
                  placeholderText={format(add(new Date(), { months: 1 }), "dd/MM/yyyy")}
                  minDate={new Date()}
                />

                <Form2.Date
                  field="followUpDate"
                  placeholderText={format(add(new Date(), { months: 1 }), "dd/MM/yyyy")}
                  validations={{ presence: true }}
                />

                <Form2.WithField<IUserRecord | undefined>
                  field="reminderUser"
                  subscribe={true}
                  render={field => (
                    <div>
                      <UserSelectList
                        label="Send reminder to"
                        onChange={(_, value) => field.setValue(value)}
                        value={field.getValue()?.id || data.quote?.customer?.sales_rep?.user?.id}
                        filter={{
                          and: [
                            { matches: { userAccountType: "branch" } },
                            { exists: { attr: "sales_rep" } },
                          ],
                        }}
                      />
                      <Spacer size={4} />
                      <P size="small" blockSpacing={0}>
                        This is the person who will be notified to check in on this project on the
                        follow up date.
                      </P>
                    </div>
                  )}
                />

                <Form2.Select
                  field="stage"
                  options={projectStages}
                  valueExtractor={(opt: { value: any }) => opt.value}
                  getOptionLabel={opt => opt.label}
                  getOptionValue={opt => opt.value}
                  validations={{ presence: true }}
                />
                <StageCertaintyWatcher />
                <Form2.Select
                  field="percentCertainty"
                  prefix="%"
                  options={percentCertaintyOptions}
                  valueExtractor={(opt: { value: any }) => opt.value}
                  getOptionLabel={opt => opt.label}
                  getOptionValue={opt => opt.value as any}
                  validations={{ presence: true }}
                />
                <ConfirmCancelButtons
                  buttonOpts={{ confirm: { children: "Create Project and Save Quote" } }}
                  onConfirm={"submit"}
                  onCancel={() => onResolve(null)}
                />
              </Form2.Form>
            )}
            {tab === "add" && (
              <Form2.Form
                onSubmit={({ project }) => onResolve({ type: "add", projectId: project.id })}
              >
                <Form2.AsyncSelect<IRecord<"projects", "name">>
                  field="project"
                  loadOptions={loadProjects}
                  validations={{ presence: true }}
                  getOptionLabel={opt => opt.name ?? ""}
                  getOptionValue={opt => opt.id}
                />
                <ConfirmCancelButtons
                  buttonOpts={{
                    confirm: { children: "Add Project" },
                  }}
                  onConfirm={"submit"}
                  onCancel={() => onResolve(null)}
                />
              </Form2.Form>
            )}
            {tab === "skip" && (
              <ConfirmCancelButtons
                buttonOpts={{
                  confirm: { children: "Skip Creating Project" },
                }}
                onConfirm={() => onResolve({ type: "skip" })}
                onCancel={() => onResolve(null)}
              />
            )}
          </div>
        )}
        isOpen
      />
    )
  })

  function ProjectButton({ disabled }: { disabled?: boolean }) {
    if (!quote) {
      return null
    }
    if (quote.projects?.[0]) {
      const project = quote.projects[0]
      return <HoverProjectLoader id={project.id} initialData={project as any} />
    }
    return (
      <CreateButton
        onClick={() => handleCreateProject()}
        buttonStyle="text"
        extendTheme={{ padding: 8, fontSize: 12 }}
        color="#666"
        disabled={disabled}
      >
        New Project
      </CreateButton>
    )
  }

  async function handleCreateProject() {
    if (!quote) {
      return
    }
    const data = await handleOpenProjectNode({ quote })
    if (!data) {
      return
    }
    if (data.type === "new") {
      const newRecord = await skeem.mutate({
        projects: {
          create: {
            name: data.name,
            customer: quote.customer ? { set: quote.customer?.id } : undefined,
            contacts: quote.contact ? { add: quote.contact?.id } : undefined,
            sales_rep: quote.customer?.sales_rep
              ? { set: quote.customer?.sales_rep?.id }
              : undefined,
            dueDate: data.dueDate,
            stage: data.stage,
            percentCertainty: data.percentCertainty,
            quotes: { set: [quote.id] },
          },
        },
      })
      try {
        const reminderUserId = data.reminderUser?.id || quote.customer?.sales_rep?.user?.id
        await skeem.mutate({
          tasks: {
            create: {
              due_at: data.followUpDate,
              body: `Reminder: follow up for project ${data.name}`,
              model: "projects",
              record_id: newRecord.id,
              status: "open",
              assigned_to: reminderUserId ? { set: [reminderUserId] } : undefined,
              created_by: { set: skeem.currentSession.id },
            },
          },
        })
      } catch (e) {
        if (newRecord.id) {
          await skeem.mutate({ projects: { destroy: newRecord.id } })
        }
        throw e
      }
    } else if (data.type === "add") {
      const project = await skeem.fetch2<IRecord<"projects", "contacts">>({
        first: { name: "projects", attributes: ["contacts"], matches: { id: data.projectId } },
      })
      await skeem.mutate({
        projects: {
          update: {
            id: data.projectId,
            quotes: { add: quote.id },
            contacts:
              quote.contact && !project.contacts.find(x => x.id === quote.contact?.id)
                ? { add: quote.contact?.id }
                : undefined,
          },
        },
      })
    } else {
      return
    }
    const qData = await loadQuote(quote.id)
    setQuote(qData)
    alerts.genericSuccess("Project assigned")
    onAfterAssign?.()
  }

  /** A controlled method for adding projects to quotes, takes customer and returns skeem mutation */
  async function handleOpenProjectNodeFromData(
    reqQuote: { customer: { id: string; sales_rep?: { user?: { id: string } } } },
    opts: IOpts,
  ): Promise<{
    query: { add: any } | { create: any } | undefined
    callback: undefined | ((id: string) => Promise<void>)
  } | null> {
    const result = await handleOpenProjectNode({ quote: reqQuote as any, opts })
    if (result?.type === "new") {
      const customer = await skeem.fetch2<IRecord<"customers", "sales_rep">>({
        first: {
          name: "customers",
          attributes: ["sales_rep"],
          filter: { matches: { id: reqQuote.customer.id } },
        },
      })
      const reminderUserId = result.reminderUser?.id || reqQuote.customer.sales_rep?.user?.id
      delete (result as any).reminderUser
      const followUpDate = result.followUpDate
      delete (result as any).followUpDate
      delete (result as any).type
      ;(result as any).customer = { set: customer.id }
      ;(result as any).sales_rep = { set: customer.sales_rep?.id ?? null }
      return {
        query: { create: { ...result } },
        callback: async (recordId: string) => {
          await skeem.mutate({
            tasks: {
              create: {
                due_at: followUpDate,
                body: `Reminder: follow up for project ${result.name}`,
                model: "projects",
                record_id: recordId,
                status: "open",
                assigned_to: reminderUserId ? { set: [reminderUserId] } : undefined,
                created_by: { set: skeem.currentSession.id },
              },
            },
          })
        },
      }
    } else if (result?.type === "add") {
      return { query: { add: result?.projectId }, callback: undefined }
    } else if (result?.type === "skip") {
      return { query: undefined, callback: undefined }
    }
    return null
  }

  return {
    projectNode,
    ProjectButton,
    handleCreateProject,
    handleOpenProjectNode: handleOpenProjectNodeFromData,
    loadQuote: async (id: string) => {
      const qData = await loadQuote(id)
      setQuote(qData)
    },
  }
}
